import React, { useState } from 'react'
import AddMessage, { AddMessageInline } from './AddMessage'
import { Accordion, AccordionTrigger, AccordionItem, AccordionContent } from 'src/components/ui/accordion'
import { isInterface } from 'src/functions/general/checks'
import { format } from 'date-fns'
import { Check, Loader2, Send, Trash2 } from 'lucide-react'
import { Input } from 'src/components/ui/input'
import TooltipForce from 'src/components/ui/tooltip-force'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/components/ui/tooltip'
import { useMutation } from 'react-query'
import axios from 'src/axios/axios'
import useAuth from 'src/hooks/useAuth'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'src/hooks/redux'
import { RootState } from 'src/store'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from 'src/components/ui/alert-dialog'

type Props = { 
    communication?: Communcation[] | null
    onSuccess?: () => void
    document?: DocumentObject
    addMessageType?: "popup" | "inline"
}

export const ApproveQuestion = ({reset, onSuccess, comm}:{reset:any, onSuccess:any, comm:Communcation}) => {
    const { auth } = useAuth()
    const { t } = useTranslation()

    const { mutate:checkQuestion, isLoading:isLoadingCheck } = useMutation({
        mutationFn: async () => {
            try {
                const { data } = await axios.put(`communications/header`, {
                    token: auth.data?.token,
                    state: 2,
                    documentquestionheaderid: comm.documentsquestionheaderid
                })
                if(data.errorcode!==0){ throw new Error(data.message) }
                reset();
                onSuccess && onSuccess()
            } catch (error) {
                console.log(error)
            }
        }
    })

    return (
        <AlertDialog>
            <Tooltip delayDuration={0}>
                
                <AlertDialogTrigger asChild>
                    <div>
                    <TooltipTrigger asChild>
                    <button className='flex items-center justify-center w-8 h-8 text-green-600 border border-transparent border-solid rounded-sm hover:border-neutral-200'>{!isLoadingCheck ? <Check className='w-4 h-4'/> : <Loader2 className='w-4 h-4 animate-spin'/> }</button>
                    </TooltipTrigger>
                    </div>
                </AlertDialogTrigger>
                
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Markeren als beantwoord</AlertDialogTitle>
                        <AlertDialogDescription>Bent u zeker deze communicatie te markeren als beantwoord?</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('Annuleren')}</AlertDialogCancel>
                        <AlertDialogAction onClick={()=>{checkQuestion()}}>Markeren als beantwoord</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>

                <TooltipContent side='left'><p className='whitespace-nowrap'>Markeren als beantwoord</p></TooltipContent>
            </Tooltip>
        </AlertDialog>
    )
}

export const RemoveQuestion = ({reset, onSuccess, comm}:{reset:any, onSuccess:any, comm:Communcation}) => {
    const { auth } = useAuth()
    const { t } = useTranslation()

    const { mutate:removeQuestion, isLoading:isLoadingRemove } = useMutation({
        mutationFn: async () => {
            try {
                const { data } = await axios.delete(`communications/header/delete?documentquestionheaderid=${comm.documentsquestionheaderid}`)
                if(data.errorcode!==0){ throw new Error(data.message) }
                reset();
                onSuccess && onSuccess()
            } catch (error) {
                console.log(error)
            }
        }
    })

    return (
        <AlertDialog>
            <Tooltip delayDuration={0}>
                
                <AlertDialogTrigger asChild>
                    <div>
                    <TooltipTrigger asChild>
                    <button className='flex items-center justify-center w-8 h-8 text-red-600 border border-transparent border-solid rounded-sm hover:border-neutral-200'>{!isLoadingRemove ? <Trash2 className='w-4 h-4'/> : <Loader2 className='w-4 h-4 animate-spin'/> }</button>
                    </TooltipTrigger>
                    </div>
                </AlertDialogTrigger>
                
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('Communicatie verwijderen')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('Bent u zeker deze communicatie te verwijderen')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('Annuleren')}</AlertDialogCancel>
                        <AlertDialogAction onClick={()=>{removeQuestion()}}>{t('Verwijderen')}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>

                <TooltipContent side='left'><p className='whitespace-nowrap'>{t('Bericht verwijderen')}</p></TooltipContent>
            </Tooltip>
        </AlertDialog>
    )
}

const Question = ({comm, onSuccess}:{comm:Communcation, onSuccess?:()=>void}) => {
    const { t } = useTranslation()
    const { auth } = useAuth()
    const colleagues:ForwardUser[] = useAppSelector((state:RootState) => state.colleagues);
    const [message, setmessage] = useState('');

    const { mutate:sendMessage, reset, isLoading } = useMutation({
        mutationFn: async () => {
            try {
                const { data } = await axios.post(`communications/message/add`, {
                    token: auth.data?.token,
                    documentquestionheaderid: comm.documentsquestionheaderid,
                    message: message
                })
                if(data.errorcode!==0){ throw new Error(data.message) }
                reset();
                setmessage('')
                onSuccess && onSuccess()
            } catch (error) {
                console.log(error)
            }
        }
    })

    function usernameToName(name:string) {
        let n = name
        const user = Array.isArray(colleagues) ? colleagues.find(u=>u.username.toLowerCase() === name.toLowerCase()) : undefined
        if(user){
            n = `${user.firstname} ${user.name}`
        }
        return n
    }

    return (
        
        <AccordionItem value={`${comm.documentsquestionheaderid}`} className="w-full">
            <AccordionTrigger className="w-full hover:no-underline">
                <div className="flex flex-col gap-2 p-2 duration-300 rounded-md cursor-pointer">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <div className="flex items-center justify-center w-6 h-6 text-xs bg-white border rounded-full min-w-6 border-sky-200">{comm.Messages.Message.filter(m=>m.inforecord===false).length || 0}</div>
                            <p className="font-medium text-left">{usernameToName(comm.createdby)}</p>
                        </div>
                        <p className="text-xs text-right line-clamp-2">{t('Laatste bericht')} {usernameToName(comm.Messages.Message[comm.Messages.Message.length-1].username)} {format(new Date(comm.lastmodif), "dd/MM/yyyy - hh:mm")}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <p className="bg-primary/20 text-primary font-medium px-2 py-0.5 rounded-sm text-sm">
                            {comm.state.toString()==='1' && t('Open')}
                            {comm.state.toString()!=='1' && t('Gesloten')}
                        </p>
                        <p className="font-semibold">{comm.title}</p>
                    </div>
                </div>
            </AccordionTrigger>
            <AccordionContent>
                <div className='flex justify-end w-full mb-4'>
                    {(!comm.rightstoclose || comm.rightstoclose === auth.data?.user.fitdocusername) && 
                    <div className='flex items-center justify-end p-1 bg-white border rounded-md'>
                        {comm.state === 1 && <ApproveQuestion comm={comm} onSuccess={onSuccess} reset={()=>{reset()}}/>}
                        <RemoveQuestion comm={comm} onSuccess={onSuccess} reset={()=>{reset()}}/>
                    </div>
                    }
                </div>

                <div className="flex flex-col w-full gap-2">
                {comm.Messages.Message.map((message, index)=>{
                    const isInfo = message.inforecord
                    const infoClass = 'bg-yellow-200'
                    const messageClass = 'bg-blue-200'
                    return (
                    <div className={`${isInfo ? 'items-start' : 'items-end'} flex flex-col w-full`} key={message.documentsquestionrecordid}>
                        <p className="text-xs text-neutral-400">{message.username && `${usernameToName(message.username)}:`} {format(new Date(message.datecreated), "dd/MM/yyyy")} - {message.timecreated.substring(0,5)}</p>
                        <div className={`${isInfo ? infoClass : messageClass} font-medium border px-2 py-1 w-fit rounded-[15px]`} key={message.documentsquestionrecordid}>
                            <p>{message.messagerecord}</p>
                        </div>
                    </div>
                    )
                })}
                </div>

                {comm.state === 1 &&
                <div className="flex items-center gap-2 mt-4">
                    <Input value={message} onChange={({currentTarget})=>{setmessage(currentTarget.value)}} placeholder={`${t('Typ een bericht')}...`} className={`w-full rounded-full`}/>
                    <button onClick={()=>{sendMessage()}} disabled={isLoading} className={`${message.length > 1 ? 'w-[40px] px-3 ' : 'w-[0px] px-0 pointer-events-none'} h-10 bg-primary text-primary-foreground rounded-full`}>
                        {!isLoading ? <Send className="w-4 h-4"/> : <Loader2 className='w-4 h-4 animate-spin'/>}
                    </button>
                </div>
                }
            </AccordionContent>
        </AccordionItem>
        
    )
}

const Communication = ({communication, document, addMessageType="popup", onSuccess}:Props) => {
  return (
    <>
        <div className="flex items-center justify-end gap-2">
            {document && 
            <>
            {addMessageType === "popup" && <AddMessage docid={document.docid} onSuccess={onSuccess}/>}
            {addMessageType === "inline" && <AddMessageInline docid={document.docid} onSuccess={onSuccess}/>}
            </>
            }
        </div>
        <Accordion type="single" className="w-full" collapsible>
        {isInterface(communication, {} as Communcation[]) && communication.map((comm, ind)=>(
            <Question comm={comm} key={comm.documentsquestionheaderid} onSuccess={()=>{onSuccess && onSuccess()}}/>
        ))}
        </Accordion>
    </>
  )
}

export default Communication